/**
 * Feature flag keys used by the website (spec 0089).
 *
 * `workspaces/observatory/platform/api/src/observatory_api/flags/registry.py` is the source of truth: it owns
 * each flag's default, owner, and removal date, and the backend is the only evaluator.
 * This lists the subset the frontend reads, so a typo is a compile error rather than a
 * silently missing flag. `test_frontend_flag_keys_are_registered` fails when a key here
 * is not in `ALL_FLAGS`.
 */
export const observatoryFlags = {
  /** Tab then Q opens the league quick navigator. */
  leagueQuickNav: "league-quick-nav",
  /**
   * DEPRECATED alias: turning it on turns on every split Website feature in `PLAY_PREVIEW_BROWSE_COVERS`
   * (it was one switch for the whole new site), so existing rules, overrides and PR-preview `FLAGS_ON`
   * keep working. Remove with the backend flag.
   */
  playPreviewBrowse: "play-preview-browse",
  /** Website: the new topnav, the one account menu and the search page. */
  websiteTopnav: "website-topnav",
  /** Website: the /play experience (champion card, round banner, setup bar, orb, corner layout). */
  websitePlay: "website-play",
  /** Website: onboarding and agent-guide copy (the desk guide, start-over.md). */
  websiteOnboarding: "website-onboarding",
  /** Website: the profile-style /account page with usage and credentials folded in. */
  websiteAccount: "website-account",
  /** Website: the Softmax tools button and Feedback launcher (team members). */
  websiteTools: "website-tools",
  /** Replay coaching (spec 0096): record narration and drawings over an Episode replay. */
  replayCoaching: "replay-coaching",
  /** Gates the "Edit this page" entry point and /<coworld>/preview/edit itself. */
  storePageEditor: "store-page-editor",
  /** Website: the /<coworld>/start wizard (and the /preview storefront's start door); while off, /start 404s for everyone. */
  previewStartTeam: "preview-start-team",
  /** The play page feed v1 (Sensei and Roger posts); while off, the play page shows no feed. */
  feedV1: "feed-v1",
} as const;
/** The Website features the deprecated `play-preview-browse` alias turns on. */
const playPreviewBrowseCovers: ReadonlySet<string> = new Set([
  observatoryFlags.websiteTopnav,
  observatoryFlags.websitePlay,
  observatoryFlags.websiteOnboarding,
  observatoryFlags.websiteAccount,
  observatoryFlags.websiteTools,
  observatoryFlags.previewStartTeam,
]);

/**
 * Whether one flag is on, from a flag read (`/flags/me`) and the forced list (`FLAGS_ON`): its own
 * value, or the deprecated `play-preview-browse` alias for the keys it covers.
 */
export function flagIsOn({
  flags,
  key,
  forced = new Set(),
}: {
  flags: Record<string, unknown> | null | undefined;
  key: string;
  forced?: ReadonlySet<string> | undefined;
}): boolean {
  if (forced.has(key) || flags?.[key] === true) return true;

  if (!playPreviewBrowseCovers.has(key)) return false;

  return (
    forced.has(observatoryFlags.playPreviewBrowse) ||
    flags?.[observatoryFlags.playPreviewBrowse] === true
  );
}

export type ObservatoryFlagKey =
  (typeof observatoryFlags)[keyof typeof observatoryFlags];
